/**
 * @author Martin Leonov <martin.leonov@ki-wi.cz>
 * @copyright Ki-Wi Digital s.r.o.
 * @license
 * All Rights Reserved.
 * Unauthorized copying of this file is strictly prohibited.
 * Proprietary and confidential.
 */

// @ts-check

/* eslint-env browser */

import { fetchWithTimeout } from '../../fetch/fetch';

/**
 * @import { TrackingEvent, VastTrack } from '.'
 * @import { IAudienceMultiplier } from '../../audience-multiplicator'
 * @import { ExternalSourceAnalyticsTracker } from '..'
 * @import { Medium } from '../../medium/medium'
 */

/** @implements {ExternalSourceAnalyticsTracker} */
export default class VastAnalyticsTracker {
  /**
   * @param {IAudienceMultiplier} audienceMultiplier
   * @param {VastTrack[]} tracks
   */
  constructor(audienceMultiplier, tracks) {
    this.audienceMultiplier = audienceMultiplier;
    this.trackingEvents = buildTrackingMap(tracks, (track) => ({
      start: track.trackingEvents?.get('start'),
      end: track.trackingEvents?.get('complete')
    }));
    this.trackingImpressions = buildTrackingMap(
      tracks,
      (track) => track.impressionUrls
    );
  }

  /**
   * @private
   * @param {Medium | undefined} medium
   * @returns {Promise<void>}
   */
  async sendImpressions(medium) {
    if (!medium) {
      return;
    }
    const audienceMultiplier = await this.audienceMultiplier
      .get()
      .catch(() => 1);

    const urls = Array.from(this.trackingImpressions.get(medium.id) ?? []);
    const repeatedRequests = Array.from({ length: audienceMultiplier }, () =>
      urls.map((url) => fetchWithTimeout(url, { method: 'GET' }))
    ).flat();
    await Promise.allSettled(repeatedRequests);
  }

  /**
   * @private
   * @param {Medium | undefined} medium
   * @param {TrackingEvent} eventType
   * @returns {Promise<void>}
   */
  async sendTracking(medium, eventType) {
    if (!medium) {
      return;
    }

    const url = this.trackingEvents.get(medium.id)?.[eventType];
    if (!url) {
      return;
    }
    await fetchWithTimeout(url, { method: 'GET' });
  }

  /** @type {ExternalSourceAnalyticsTracker['trackStartAnalytics']} */
  async trackStartAnalytics(content) {
    const medium = content.medium;

    await this.sendImpressions(medium).catch(() => {
      // TODO: jak ošetřit případně chyby
    });
    await this.sendTracking(medium, 'start').catch(() => {
      // TODO: jak ošetřit případně chyby
    });
  }

  /** @type {ExternalSourceAnalyticsTracker['trackEndAnalytics']} */
  async trackEndAnalytics(content) {
    const medium = content.medium;
    await this.sendTracking(medium, 'complete').catch(() => {
      // TODO: jak ošetřit případně chyby
    });
  }
}

/**
 * @template T
 * @param {VastTrack[]} tracks
 * @param {(track: VastTrack) => T} mapper
 * @returns {Map<string, T>}
 */
function buildTrackingMap(tracks, mapper) {
  /** @type {Map<string, T>} */
  const map = new Map();

  for (const track of tracks) {
    if (track.mediumId && track.trackingEvents) {
      map.set(track.mediumId, mapper(track));
    }
  }

  return map;
}
